
import { useCallback, useRef, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { presenceService } from '@/core/PresenceService';

interface UseOptimizedPresenceProps {
  conversationId: string | null;
  userId: string | null;
  partnerId: string | null;
}

interface PartnerPresence {
  presenceStatus: string;
  lastSeen: string | null;
  lastMessageTime: string | null;
  mostRecentActivity: string | null;
}

export const useOptimizedPresence = ({ conversationId, userId, partnerId }: UseOptimizedPresenceProps) => {
  const lastUpdateRef = useRef<number>(0);
  const currentStatusRef = useRef<string>('offline');
  const [partnerPresence, setPartnerPresence] = useState<PartnerPresence>({
    presenceStatus: 'offline',
    lastSeen: null,
    lastMessageTime: null,
    mostRecentActivity: null
  });
  
  const updatePresenceStatus = useCallback(async (status: 'online' | 'typing' | 'recording audio' | 'offline') => {
    if (!userId) return;
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;
    
    // Rate limiting: don't update more than once every 2 seconds for same status
    if (currentStatusRef.current === status && timeSinceLastUpdate < 2000) {
      return;
    }
    
    try {
      currentStatusRef.current = status;
      lastUpdateRef.current = now;
      
      debugLogger.debug('OptimizedPresence: Updating status to:', status);
      
      // Update general presence via PresenceService
      await presenceService.updatePresence(
        userId,
        status === 'offline' ? 'going_offline' : 'presence_update'
      );
      
      // Update specific conversation presence if in a conversation
      if (conversationId && partnerId) {
        const { error: convError } = await supabase
          .from('conversations')
          .update({
            [`user${userId === conversationId ? '1' : '2'}_presence_status`]: status
          })
          .eq('id', conversationId);
          
        if (convError) {
          debugLogger.error('OptimizedPresence: Error updating conversation presence:', convError);
        }
      }
      
      // If going offline, update is_online status
      if (status === 'offline') {
        await supabase
          .from('profiles')
          .update({ 
            is_online: false,
            last_seen: new Date().toISOString()
          })
          .eq('id', userId);
      }
      
    } catch (error: any) {
      // Log como warning - presence não deve quebrar funcionalidade
      debugLogger.warn('OptimizedPresence: Could not update presence status:', {
        status,
        error: error?.message,
        code: error?.code
      });
    }
  }, [userId, conversationId, partnerId]);

  const markUserActivity = useCallback(async (activityType: string = 'general') => {
    if (!userId) return;
    
    try {
      await presenceService.updatePresence(userId, activityType);
    } catch (error: any) {
      // Log como warning - activity marking não deve quebrar funcionalidade
      debugLogger.warn('OptimizedPresence: Could not mark activity:', {
        activityType,
        error: error?.message,
        code: error?.code
      });
    }
  }, [userId]);

  // Fetch partner presence data
  const fetchPartnerPresence = useCallback(async () => {
    if (!partnerId) return;
    
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_online, last_seen, activity_type')
        .eq('id', partnerId)
        .single();
        
      if (error) {
        debugLogger.error('OptimizedPresence: Error fetching partner presence:', error);
        return;
      }
      
      // Get conversation presence if available
      let conversationStatus = 'offline';
      if (conversationId) {
        const { data: conversation } = await supabase
          .from('conversations')
          .select('user1_id, user2_id, user1_presence_status, user2_presence_status, last_message_time')
          .eq('id', conversationId)
          .single();
          
        if (conversation) {
          conversationStatus = conversation.user1_id === partnerId 
            ? conversation.user1_presence_status 
            : conversation.user2_presence_status;
            
          setPartnerPresence({
            presenceStatus: profile?.is_online ? conversationStatus || 'online' : 'offline',
            lastSeen: profile?.last_seen || null,
            lastMessageTime: conversation.last_message_time || null,
            mostRecentActivity: profile?.activity_type || null
          });
          return;
        }
      }
      
      setPartnerPresence({
        presenceStatus: profile?.is_online ? 'online' : 'offline',
        lastSeen: profile?.last_seen || null,
        lastMessageTime: null,
        mostRecentActivity: profile?.activity_type || null
      });
      
    } catch (error) {
      debugLogger.error('OptimizedPresence: Error fetching partner presence:', error);
    }
  }, [partnerId, conversationId]);

  // Subscribe to partner presence changes
  useEffect(() => {
    if (!partnerId) return;
    
    fetchPartnerPresence();
    
    const channel = supabase
      .channel(`partner-presence-${partnerId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${partnerId}`
        },
        () => {
          fetchPartnerPresence();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [partnerId, fetchPartnerPresence]);

  return {
    updatePresenceStatus,
    markUserActivity,
    partnerPresence
  };
};
